import React, { useState } from 'react';
import MathHtml from '../../components/shared/MathHtml';
import { useQuery } from '@tanstack/react-query';
import { getDailyTargets, startPractice, submitPractice, getCreatedModuleTests } from '../../api';

// Match the rest of the app: question/option/explanation text may contain HTML
// (subscripts, Greek letters, entities). Render it the same way TestResult does.
function toHtml(s) { const t = s || ''; return t.indexOf('<') >= 0 ? t : t.split('\n').join('<br>'); }
function HtmlDiv(p) { return React.createElement(MathHtml, { tag: 'div', style: p.style, html: toHtml(p.html) }); }
function HtmlSpan(p) { return React.createElement(MathHtml, { tag: 'span', style: p.style, html: toHtml(p.html) }); }

const OPTS = ['A', 'B', 'C', 'D'];

function ScorePill({ pct }) {
  if (pct == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const cls = pct >= 80 ? 'badge-green' : pct < 50 ? 'badge-red' : 'badge-yellow';
  return <span className={`badge ${cls}`}>{Math.round(pct)}%</span>;
}

export default function DailyTarget() {
  const [view, setView] = useState('home');      // home | quiz | result
  const [tab, setTab] = useState('pending');     // pending | all
  const [quiz, setQuiz] = useState(null);         // { module_code, module_name, questions }
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dailyTargets'],
    queryFn: () => getDailyTargets().then(r => r.data),
  });

  // Every module test ever created, so any of them can be retaken at will.
  const { data: created, refetch: refetchCreated } = useQuery({
    queryKey: ['createdModuleTests'],
    queryFn: () => getCreatedModuleTests().then(r => r.data),
  });

  const begin = async (code) => {
    setErr(''); setBusy(true);
    try {
      const r = await startPractice(code);
      setQuiz(r.data); setAnswers({}); setResult(null); setView('quiz');
      window.scrollTo(0, 0);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not start the quiz.');
    } finally { setBusy(false); }
  };

  const finish = async () => {
    const unanswered = quiz.questions.filter(q => !answers[q.id]).length;
    if (unanswered > 0 && !window.confirm(`${unanswered} question(s) not answered — they'll be marked wrong. Submit anyway?`)) return;
    setBusy(true); setErr('');
    try {
      const payload = { test_id: quiz.test_id, answers: quiz.questions.map(q => ({ question_id: q.id, answer: answers[q.id] || null })) };
      const r = await submitPractice(payload);
      setResult(r.data); setView('result'); refetch(); refetchCreated(); window.scrollTo(0, 0);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not submit.');
    } finally { setBusy(false); }
  };

  // ---------- QUIZ ----------
  if (view === 'quiz' && quiz) {
    const answered = Object.keys(answers).length;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Practice quiz</div>
            <h1 style={{ fontSize: 22 }}>{quiz.module_name}</h1>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setView('home')}>Cancel</button>
        </div>
        {err && <div className="card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)', marginBottom: 16 }}>{err}</div>}
        {quiz.questions.map((q, i) => (
          <div className="card" key={q.id} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Question {i + 1} of {quiz.questions.length}</div>
            {q.passage_text && <HtmlDiv html={q.passage_text} style={{ background: 'var(--primary-light)', borderRadius: 8, padding: '8px 12px', marginBottom: 8, fontSize: 14 }} />}
            <HtmlDiv html={q.question_text} style={{ fontSize: 16, fontWeight: 500, marginBottom: 12, lineHeight: 1.7 }} />
            {q.question_image_url && <img src={q.question_image_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 10 }} />}
            {OPTS.map(k => {
              const txt = q[`option_${k.toLowerCase()}`];
              const img = q[`option_${k.toLowerCase()}_image_url`];
              if (txt == null && !img) return null;
              const sel = answers[q.id] === k;
              return (
                <div key={k} onClick={() => setAnswers(a => ({ ...a, [q.id]: k }))}
                  style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 12px', margin: '8px 0',
                    border: `1.5px solid ${sel ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 10, cursor: 'pointer',
                    background: sel ? 'var(--primary-light)' : 'var(--surface)',
                  }}>
                  <span style={{ fontWeight: 700, color: 'var(--primary)' }}>{k}</span>
                  <div><HtmlSpan html={txt} />{img && <img src={img} alt="" style={{ maxWidth: '100%', borderRadius: 6, marginTop: 4 }} />}</div>
                </div>
              );
            })}
          </div>
        ))}
        <div style={{ position: 'sticky', bottom: 0, padding: '12px 0', background: 'linear-gradient(0deg, var(--bg), transparent)' }}>
          <button className="btn btn-primary" style={{ width: '100%' }} disabled={busy} onClick={finish}>
            {busy ? 'Submitting…' : `Submit (${answered}/${quiz.questions.length} answered)`}
          </button>
        </div>
      </div>
    );
  }

  // ---------- RESULT ----------
  if (view === 'result' && result) {
    return (
      <div>
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h1 style={{ fontSize: 22 }}>{quiz?.module_name}</h1>
            {result.learned && <span className="badge badge-blue">Learned 🎓</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginTop: 16 }}>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: result.score_pct >= 80 ? 'var(--success)' : result.score_pct < 50 ? 'var(--danger)' : 'var(--warning)' }}>{Math.round(result.score_pct)}%</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Score</div>
            </div>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 800 }}>{result.correct}/{result.total}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Correct</div>
            </div>
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: result.practice_score === 0 ? 'var(--success)' : 'var(--danger)' }}>{result.practice_score}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Module score (wrong×2)</div>
            </div>
          </div>
          <p style={{ color: 'var(--text-muted)', marginTop: 12, fontSize: 14 }}>
            {result.learned ? 'All correct — this module is marked learned and drops out of your targets.' : 'Review the ones you missed below. This module stays in your practice list.'}
          </p>
        </div>
        {result.review.map((q, i) => (
          <div className="card" key={q.question_id} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, marginBottom: 6, fontWeight: 600, color: q.is_correct ? 'var(--success)' : (q.is_attempted ? 'var(--danger)' : 'var(--warning)') }}>
              Q{i + 1} · {q.is_correct ? 'Correct' : (q.is_attempted ? 'Wrong' : 'Skipped')}
            </div>
            <HtmlDiv html={q.question_text} style={{ fontSize: 15, fontWeight: 500, marginBottom: 10, lineHeight: 1.7 }} />
            {OPTS.map(k => {
              const txt = q[`option_${k.toLowerCase()}`];
              if (txt == null) return null;
              const isCorrect = k === q.correct_option;
              const isYours = k === q.your_answer;
              return (
                <div key={k} style={{
                  display: 'flex', gap: 10, padding: '9px 12px', margin: '6px 0', borderRadius: 8,
                  border: `1.5px solid ${isCorrect ? 'var(--success)' : isYours ? 'var(--danger)' : 'var(--border)'}`,
                  background: isCorrect ? '#ECFDF5' : isYours ? '#FEF2F2' : 'var(--surface)',
                }}>
                  <span style={{ fontWeight: 700 }}>{k}</span><div><HtmlSpan html={txt} /></div>
                </div>
              );
            })}
            {q.explanation && <div style={{ background: 'var(--primary-light)', borderRadius: 8, padding: '10px 12px', marginTop: 8, fontSize: 14, lineHeight: 1.7 }}><b>Why:</b> <HtmlSpan html={q.explanation} /></div>}
          </div>
        ))}
        <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => { setView('home'); setQuiz(null); setResult(null); }}>Back to module tests</button>
      </div>
    );
  }

  // ---------- HOME ----------
  const targets = data?.targets || [];
  const taken = data?.taken || [];
  const pending = data?.pending || [];
  const doneCt = targets.filter(t => t.practiced).length;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28 }}>🎯 Module Tests</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          Five tests are waiting for you at a time. A new one is only created once you
          finish one — so the list never piles up.
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {[['pending', `Pending Tests (${(data?.targets || []).filter(t => !t.practiced).length})`],
            ['all', `All Tests (${(created?.module_tests || []).length})`]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{
                padding: '8px 16px', borderRadius: 999, fontSize: 14, cursor: 'pointer',
                border: '1px solid ' + (tab === key ? 'var(--primary)' : 'var(--border)'),
                background: tab === key ? 'var(--primary)' : 'transparent',
                color: tab === key ? 'white' : 'var(--text-muted)',
                fontWeight: tab === key ? 600 : 400,
              }}>{label}</button>
          ))}
        </div>
      </div>

      {err && <div className="card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)', marginBottom: 16 }}>{err}</div>}

      {isLoading ? <div className="spinner"></div> : (
        <>
          {tab === 'pending' && <>
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Your 5 weakest modules right now</span>
              <span className={`badge ${doneCt === targets.length && targets.length ? 'badge-green' : 'badge-yellow'}`}>{doneCt}/{targets.length} done</span>
            </div>
            {targets.length === 0 ? (
              <div className="empty-state" style={{ padding: '24px 0' }}>🎉 No weak modules to practise right now. Great job!</div>
            ) : targets.map((t, ti) => (
              <div key={t.module_code} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--primary-light)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>{t.practiced ? '✓' : ti + 1}</div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{t.module_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Class {t.class} · {t.error_points} error point{t.error_points === 1 ? '' : 's'} · {t.question_count} Qs</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right', minWidth: 120 }}>
                  {t.practiced ? (
                    <>
                      <div><span className="badge badge-green">Done · {t.score_pct != null ? Math.round(t.score_pct) + '%' : '—'}</span></div>
                      <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }} disabled={busy} onClick={() => begin(t.module_code)}>Redo</button>
                    </>
                  ) : (
                    <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => begin(t.module_code)}>Practise Now</button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 18, marginBottom: 12 }}>Recently Practised</h2>
            {taken.length === 0 ? <div className="empty-state">No modules practised yet.</div> : (
              <table className="table">
                <thead><tr><th>Module</th><th style={{ textAlign: 'right' }}>Score</th></tr></thead>
                <tbody>
                  {taken.slice(0, 20).map(t => (
                    <tr key={t.module_code}>
                      <td>{t.module_name}<div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Class {t.class}{t.learned ? ' · Learned 🎓' : ''}</div></td>
                      <td style={{ textAlign: 'right' }}><ScorePill pct={t.score_pct} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          </>}

          {tab === 'all' && <>
          <div className="card" style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 18, marginBottom: 4 }}>All Module Tests Created</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
              Every module test ever made for you. Take any of them again, as many times as you like.
            </p>
            {(created?.module_tests || []).length === 0 ? (
              <div className="empty-state">No module tests created yet.</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th style={{ textAlign: 'center' }}>Taken</th>
                    <th style={{ textAlign: 'right' }}>Best</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(created?.module_tests || []).map(m => (
                    <tr key={m.module_code}>
                      <td>
                        {m.module_name}
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          Class {m.class} · {m.question_count} Qs
                          {m.is_open ? ' · not taken yet' : ''}
                          {m.learned ? ' · Learned 🎓' : ''}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>{m.attempts || 0}×</td>
                      <td style={{ textAlign: 'right' }}><ScorePill pct={m.best_accuracy} /></td>
                      <td style={{ textAlign: 'right' }}>
                        <button className={`btn btn-sm ${m.is_open ? 'btn-primary' : 'btn-ghost'}`}
                                disabled={busy} onClick={() => begin(m.module_code)}>
                          {m.is_open ? 'Practise Now' : 'Take again'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card">
            <h2 style={{ fontSize: 18, marginBottom: 12 }}>Assigned but Not Yet Taken</h2>
            {pending.length === 0 ? <div className="empty-state">Nothing pending.</div> : (
              <table className="table">
                <thead><tr><th>Module</th><th style={{ textAlign: 'right' }}>Pending since</th></tr></thead>
                <tbody>
                  {pending.slice(0, 40).map(p => (
                    <tr key={p.module_code}>
                      <td>{p.module_name}<div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Class {p.class}</div></td>
                      <td style={{ textAlign: 'right', color: 'var(--warning)' }}>{p.pending_since ? new Date(p.pending_since).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          </>}
        </>
      )}
    </div>
  );
}
